<template>
    <div class="location-delivery-wrapper" data-cy="delivery-section">
        <orderTypeToggle :showToolbar="!isMobile" v-model="isDelivery" data-cy="order-toggle" />
        <h4 class="ion-margin-start bold color-black">Deliver to</h4>

        <!-- Saved Addresses + Form -->
        <addressList v-if="isLoggedIn" :addresses="deliveryAddresses" selectable editable deleteable addBtn @updateShowAddressForm="updateShowAddressForm" @selected="selectDeliveryAddress" />

        <!-- Guest New Address -->
        <addressForm
            v-else-if="!foundAddress"
            submitBtnText="Find My Delivery Store"
            :submitFunction="validate"
            :initialValues="getSearchedDeliveryAddress"
            @updatingAddress="updatingAddress"
            @changePickup="$emit('changePickup')"
        />

        <!-- Guest Compact Found-Address -->
        <ion-row v-else class="compressed-address ion-justify-content-between ion-align-items-center ion-align-content-center">
            <ion-col size="10">
                <span>{{ getSearchedDeliveryAddress.address1 }} {{ getSearchedDeliveryAddress.address2 }}</span>
                <br />
                <span>{{ getSearchedDeliveryAddress.city }}, {{ getSearchedDeliveryAddress.state }} {{ getSearchedDeliveryAddress.zip }}</span>
            </ion-col>
            <ion-col size="2" style="cursor: pointer; text-align: center; margin-top: 5px">
                <ion-icon :icon="closeOutline" class="bold-icon" v-on:click="foundAddress = false"></ion-icon>
            </ion-col>
        </ion-row>

        <!-- Error Notice -->
        <locationDeliveryErrorCard v-if="errorMsg" isMobile :errorMsg="errorMsg" :deliveryAddress="searchedAddress" class="ion-margin-bottom" @changePickup="$emit('changePickup')" />

        <!-- Estimated Arrival Time -->
        <locationDeliveryCard
            v-if="showDeliveryInfo"
            isDelivery
            isMobile
            :storeLocation="getDisplayDeliveryStore"
            :deliveryAddress="getSearchedDeliveryAddress"
            class="ion-margin-bottom"
            style="margin-bottom: 80px"
            :isDeliveryTurnedOff="isDeliveryTurnedOff"
            :isDeliveryTurnedOn="!isDeliveryTurnedOff"
            :isStoreOpen="isStoreOpen"
            @deliverHere="selectLocation(getDisplayDeliveryStore.id)"
            @changePickup="$emit('changePickup')"
        />

        <!-- Deliver Here -->
        <ion-row class="ion-no-margin force-bottom full-width z-top">
            <ion-button
                v-if="showDeliveryInfo"
                @click="selectLocation(getDisplayDeliveryStore.id)"
                color="primary"
                expand="full"
                class="ion-no-margin force-bottom full-width z-top"
                size="large"
                data-cy="button-delivery-here"
                id="button-delivery-here"
                :disabled="processingNearLocations || showAddAddressForm || isSubmitDisabled || isDeliveryTurnedOff"
            >
                <ion-spinner v-if="state.isDeliveryLoading" name="lines-small" />
                <div v-else>{{ isRedRobinStore ? 'Order at Red Robin' : 'Deliver here' }}</div>
            </ion-button>
        </ion-row>
    </div>
</template>

<script lang="ts">
import { defineComponent, computed, ref, watch, nextTick, reactive } from 'vue';
import { IonButton, IonSpinner, IonRow, IonCol, IonIcon } from '@ionic/vue';
import { closeOutline } from 'ionicons/icons';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';

import addressForm from '@/components/forms/addressForm.vue';
import addressList from '@/components/addressList.vue';
import locationDeliveryCard from '@/components/locationDeliveryCard.vue';
import locationDeliveryErrorCard from '@/components/locationDeliveryErrorCard.vue';
import { Address } from '@/models/addressModels';
import { isStoreCurrentlyOpen, validateDeliveryAddress } from '@/services/locationService';
import { errorToast } from '@/services/pageService';
import { alertController } from '@ionic/vue';
import orderTypeToggle from '@/components/orderTypeToggle.vue';

export default defineComponent({
    props: { reload: Boolean },
    components: {
        addressList,
        addressForm,
        locationDeliveryCard,
        locationDeliveryErrorCard,
        IonButton,
        IonSpinner,
        IonRow,
        orderTypeToggle,
        IonIcon,
        IonCol,
    },
    emits: ['changePickup'],
    setup(props, { emit }) {
        const state = reactive({
            isDeliveryLoading: false,
        });
        const store = useStore();
        const route = useRoute();
        const router = useRouter();
        const isRedRobinStore = ref(false);

        const getDisplayDeliveryStore = computed(() => store.getters.getDisplayDeliveryStore);
        const getSearchedDeliveryAddress = computed(() => store.getters.getSearchedDeliveryAddress);
        const deliveryAddresses = computed(() => store.getters.getAccountAddresses);
        const isLoggedIn = computed(() => store.getters.isLoggedIn);
        const processingNearLocations = computed(() => store.getters.processingNearLocations);
        const isMobile = computed(() => store.getters.isMobile);
        const errorMsg = ref();
        const submissionError = ref();
        const foundAddress = ref(!!getDisplayDeliveryStore.value);

        // this is for error use cases only, not saved to store;
        const searchedAddress = ref();

        const isDeliveryTurnedOff = computed(() => getDisplayDeliveryStore.value?.storeStatus?.isDelTurnedOff ?? false);
        const isStoreOpen = computed(() => (getDisplayDeliveryStore.value?.hours ? isStoreCurrentlyOpen(getDisplayDeliveryStore.value.hours) : false));

        const isDeliveryAvailable = computed(() => {
            if (isDeliveryTurnedOff.value) return false;
            return isStoreOpen;
        });

        const isDelivery = ref(true);
        watch(isDelivery, (value) => {
            if (value == false) emit('changePickup');
        });

        const showDeliveryInfo = computed(() => {
            const deliveryStoreFound = !!getDisplayDeliveryStore.value;
            const isStoreOpen = !getDisplayDeliveryStore?.value?.isTempClosed;
            const noStoreErrors = !errorMsg.value;
            const addressFormHidden = !showAddAddressForm.value;
            // foundAddress can be false if isLogged in is true
            const guestFoundAddress = foundAddress.value || isLoggedIn.value;
            const userHasAddresses = deliveryAddresses.value.length > 0 || !isLoggedIn.value;

            return deliveryStoreFound && isStoreOpen && noStoreErrors && addressFormHidden && guestFoundAddress && userHasAddresses;
        });

        const isSubmitDisabled = ref(false);

        const showAddAddressForm = ref(false);

        const selectDeliveryAddress = (address) => {
            if (showAddAddressForm.value) return;
            store.dispatch('foundDeliveryLocation', address);
            isSubmitDisabled.value = false;
        };

        const updateShowAddressForm = (status) => {
            showAddAddressForm.value = status;
        };

        const validate = async (address: Address) => {
            errorMsg.value = undefined;
            searchedAddress.value = address;
            await validateDeliveryAddress(address)
                .then(async (valid) => {
                    if (valid.message.includes('address is corrected.')) {
                        const address1 = valid.address.address1;
                        const address2 = valid.address.address2 ?? '';
                        const city = valid.address.city ?? '';
                        const state = valid.address.state ?? '';
                        const zipCode = valid.address.zip ?? valid.address.zipcode ?? '';

                        const alert = await alertController.create({
                            header: `Address Suggestion`,
                            message: `${address1} ${address2}<br>${city}, ${state} ${zipCode}`,
                            backdropDismiss: true,
                            cssClass: 'valid-address-alert',
                            htmlAttributes: { ['style']: 'width: 500px;' },
                            buttons: [
                                {
                                    text: 'Check Address',
                                    role: 'cancel',
                                    handler: () => {
                                        isSubmitDisabled.value = true;
                                    },
                                },
                                {
                                    text: 'Use This Address',
                                    role: 'confirm',
                                    handler: () => {
                                        store.dispatch('foundDeliveryLocation', valid);
                                        foundAddress.value = true;
                                        nextTick(() => (isSubmitDisabled.value = false));
                                    },
                                },
                            ],
                        });
                        return alert.present();
                    } else {
                        await store.dispatch('foundDeliveryLocation', valid);
                        foundAddress.value = true;
                        isSubmitDisabled.value = false;
                    }
                })
                .catch((err) => (errorMsg.value = err));
        };

        const updatingAddress = () => {
            isSubmitDisabled.value = true;
        };

        const addAddress = async (form) =>
            await store
                .dispatch('addUserAddress', form)
                .then(() => (showAddAddressForm.value = false))
                .catch((err) => (submissionError.value = err));

        const selectLocation = async (locationId) => {
            state.isDeliveryLoading = true;

            try {
                if (getDisplayDeliveryStore.value?.storeStatus?.isRedRobinStore) {
                    router.push({
                        name: 'redRobin',
                        query: {
                            name: getDisplayDeliveryStore.value.name,
                            slug: getDisplayDeliveryStore.value.slug,
                            address1: getDisplayDeliveryStore.value.address.address1,
                            address2: getDisplayDeliveryStore.value.address.address2,
                            city: getDisplayDeliveryStore.value.address.city,
                            state: getDisplayDeliveryStore.value.address.state,
                            zip: getDisplayDeliveryStore.value.address.zip,
                            phone: getDisplayDeliveryStore.value.phone,
                        },
                    });
                    return;
                }

                await store.dispatch('selectDeliveryLocation', locationId);
                //@ts-ignore
                router.push(route.query?.redirect ?? '/menu');
            } catch (err: any) {
                errorToast(err);
            } finally {
                state.isDeliveryLoading = false;
            }
        };

        watch(
            () => props.reload,
            (reload) => {
                if (reload && !getDisplayDeliveryStore.value && deliveryAddresses.value.length > 0) {
                    selectDeliveryAddress(deliveryAddresses.value[0]);
                }
            }
        );

        watch(errorMsg, (errorMsg) => {
            if (errorMsg) {
                const scrollContent = document.getElementById('pickupAndDeliveryContent');
                setTimeout(() => {
                    scrollContent?.scrollTo({
                        top: scrollContent?.scrollHeight * 4,
                        behavior: 'smooth',
                    });
                }, 100);
            }
        });

        watch(getDisplayDeliveryStore, (store) => {
            isRedRobinStore.value = store?.storeStatus?.isRedRobinStore;
        });

        return {
            state,
            selectDeliveryAddress,
            selectLocation,
            validate,
            addAddress,
            getDisplayDeliveryStore,
            getSearchedDeliveryAddress,
            deliveryAddresses,
            isLoggedIn,
            isMobile,
            isDeliveryAvailable,
            processingNearLocations,
            errorMsg,
            submissionError,
            showAddAddressForm,
            updatingAddress,
            isSubmitDisabled,
            isDeliveryTurnedOff,
            searchedAddress,
            isStoreOpen,
            isDelivery,
            foundAddress,
            closeOutline,
            updateShowAddressForm,
            showDeliveryInfo,
            isRedRobinStore,
        };
    },
});
</script>

<style scoped>
.location-delivery-wrapper {
    overflow-y: auto;
}

@media screen and (min-width: 960px) {
    .location-delivery-wrapper,
    .location-delivery-row {
        height: 100%;
    }

    .delivery-location {
        padding: 10px 20px;
    }
}

@media screen and (max-width: 960px) {
    .force-bottom {
        position: fixed !important;
    }
}

.bold-icon {
    --ionicon-stroke-width: 64px;
    font-size: 1.5em;
    color: var(--ion-color-medium-shade);
}
.bold-icon:hover {
    color: var(--ion-color-medium-contrast);
}

.compressed-address {
    margin: 10px;
    padding: 5px;
    border: 1px solid #d7d8da;
    border-radius: 12px;
    font-size: 1.4em;
    font-weight: bold;
    justify-content: space-between;
}
</style>
